// Daily-work on-site / off-site flow shared by the web and mobile worker
// screens (SHY-183). Workers are independent contractors, so the product
// language is "on-site" / "off-site" — never "clock in" / "clock out" — even
// though the database columns remain `clock_in` / `clock_out`. A worker may
// not go off-site until every task is done and notes are written; both apps
// call getOffSiteLockMessage so the rule and its message stay identical.

export const ON_SITE_LABEL = "On-site";
export const OFF_SITE_LABEL = "Off-site";
export const ON_SITE_ACTION = "I'm On-site";
export const OFF_SITE_ACTION = "I'm Off-site";

export type TaskDoneLike = { done: boolean };

export type OffSiteGateInput = {
  tasks: readonly TaskDoneLike[];
  notes: string | null | undefined;
};

export function hasNotes(notes: string | null | undefined): boolean {
  return typeof notes === "string" && notes.trim().length > 0;
}

function allTasksDone(tasks: readonly TaskDoneLike[]): boolean {
  return tasks.every((task) => task.done);
}

// Returns the reason the worker cannot go off-site yet, or null when they can.
export function getOffSiteLockMessage(input: OffSiteGateInput): string | null {
  const tasksMissing = !allTasksDone(input.tasks);
  const notesMissing = !hasNotes(input.notes);
  if (tasksMissing && notesMissing) return "Complete all tasks and add notes before going off-site";
  if (tasksMissing) return "Complete all tasks before going off-site";
  if (notesMissing) return "Add notes before going off-site";
  return null;
}

// Status line under each time; `formattedTime` is already rendered for display.
export function formatSiteStatus(kind: "on" | "off", formattedTime: string | null): string {
  if (!formattedTime) return kind === "on" ? "Not on-site yet" : "Not off-site yet";
  return kind === "on" ? `On-site at ${formattedTime}` : `Off-site at ${formattedTime}`;
}

export const WORK_DAY_TIME_ZONE = "America/New_York";

const workDayCalendar = new Intl.DateTimeFormat("en-US", { timeZone: WORK_DAY_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" });

export function workDayDateAt(at: Date): string {
  const part = Object.fromEntries(workDayCalendar.formatToParts(at).map(({ type, value }) => [type, value]));
  return `${part.year}-${part.month}-${part.day}`;
}

const DAY_MS = 86_400_000;

export function workDayDateAfter(days: number, at: Date = new Date()): string {
  return new Date(Date.parse(`${workDayDateAt(at)}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

export const isWorkedToday = (workDate: string, now: Date = new Date()): boolean => workDate === workDayDateAt(now);
